import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stack,
  ListItemButton,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  CheckCircle as PaidIcon,
} from '@mui/icons-material';
import { format, isPast } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { WidgetCard } from './WidgetCard';
import { WidgetItemsModal } from './WidgetItemsModal';
import { MoreItemsBadge } from './MoreItemsBadge';
import { api } from '../../../services/api';

const MAX_VISIBLE_ITEMS = 3;

interface Invoice {
  _id: string;
  invoiceNumber: string;
  clientId: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  total: number;
  status: string;
  dueDate: string;
}

export const PendingInvoicesWidget: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['invoices', 'pending'],
    queryFn: async () => {
      const response = await api.get('/invoices', {
        params: {
          status: 'sent',
          limit: 20,
          sortBy: 'dueDate',
          sortOrder: 'asc',
        },
      });
      return response.data;
    },
  });

  const invoices: Invoice[] = data?.data?.invoices || [];
  const visibleInvoices = invoices.slice(0, MAX_VISIBLE_ITEMS);
  const remainingCount = Math.max(0, invoices.length - MAX_VISIBLE_ITEMS);

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const isOverdue = (dueDate: string) => {
    return isPast(new Date(dueDate));
  };

  const handleInvoiceClick = (invoiceId: string) => {
    setModalOpen(false);
    navigate(`/invoices/${invoiceId}`);
  };

  const InvoiceListItem = ({ invoice, inModal = false }: { invoice: Invoice; inModal?: boolean }) => {
    const overdue = isOverdue(invoice.dueDate);
    return (
      <ListItem
        disablePadding
        sx={{
          borderLeft: overdue ? '3px solid' : 'none',
          borderColor: 'error.main',
          bgcolor: overdue ? 'error.50' : 'transparent',
          borderRadius: 1,
          mb: 0.5,
        }}
      >
        <ListItemButton
          onClick={() => handleInvoiceClick(invoice._id)}
          sx={{
            py: inModal ? 1.5 : 0.75,
            pl: overdue ? 1 : (inModal ? 2 : 0),
            pr: inModal ? 2 : 0,
            '&:hover': {
              backgroundColor: overdue ? 'error.100' : 'action.hover',
            },
          }}
        >
          <ListItemText
            primary={
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                  variant="body2"
                  fontWeight={500}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: inModal ? 200 : 140,
                  }}
                >
                  {invoice.clientId
                    ? `${invoice.clientId.firstName} ${invoice.clientId.lastName}`
                    : 'Unknown'}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={overdue ? 'error.main' : 'text.primary'}
                >
                  {formatCurrency(invoice.total)}
                </Typography>
              </Stack>
            }
            secondary={
              <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.25}>
                <Typography variant="caption" color="text.secondary">
                  #{invoice.invoiceNumber}
                </Typography>
                <Chip
                  label={overdue ? 'Overdue' : `Due ${format(new Date(invoice.dueDate), 'MMM d')}`}
                  size="small"
                  color={overdue ? 'error' : 'default'}
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              </Stack>
            }
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const EmptyState = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100%"
      py={2}
    >
      <PaidIcon sx={{ fontSize: 48, color: 'success.light', mb: 1 }} />
      <Typography variant="body2" color="text.secondary">
        No pending invoices
      </Typography>
      <Typography variant="caption" color="text.secondary">
        All invoices are paid!
      </Typography>
    </Box>
  );

  // Calculate total pending
  const totalPending = invoices.reduce((sum, inv) => sum + inv.total, 0);

  return (
    <>
      <WidgetCard
        title="Pending Invoices"
        icon={<ReceiptIcon color="warning" />}
        isLoading={isLoading}
        error={error as Error | null}
        onRefresh={() => refetch()}
        viewAllLink="/invoices"
        viewAllLabel="View All Invoices"
        minHeight={180}
        action={
          totalPending > 0 ? (
            <Chip
              label={formatCurrency(totalPending)}
              color="warning"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          ) : undefined
        }
      >
        {invoices.length === 0 ? (
          <EmptyState />
        ) : (
          <Box display="flex" flexDirection="column" height="100%">
            <List dense disablePadding sx={{ flex: 1 }}>
              {visibleInvoices.map((invoice) => (
                <InvoiceListItem key={invoice._id} invoice={invoice} />
              ))}
            </List>
            {remainingCount > 0 && (
              <Box display="flex" justifyContent="center" pt={1}>
                <MoreItemsBadge
                  count={remainingCount}
                  onClick={() => setModalOpen(true)}
                  color="warning"
                  label={`+${remainingCount} more invoices. Click to view all`}
                />
              </Box>
            )}
          </Box>
        )}
      </WidgetCard>

      <WidgetItemsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Pending Invoices"
        icon={<ReceiptIcon color="warning" />}
        totalCount={invoices.length}
      >
        {invoices.map((invoice) => (
          <InvoiceListItem key={invoice._id} invoice={invoice} inModal />
        ))}
      </WidgetItemsModal>
    </>
  );
};

export default PendingInvoicesWidget;
